import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from "../hooks/useToast";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '../components/common/Card';
import { Button } from '../components/common/Button';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../components/common/Tabs';
import {
  CalendarIcon,
  ChevronLeftIcon,
  StopIcon,
  CheckCircledIcon,
  CrossCircledIcon,
  TimerIcon,
  ClockIcon,
  CheckIcon,
  Cross1Icon,
  ArrowLeftIcon,
} from '@radix-ui/react-icons';
import { 
  getActivity, 
  getActivityExtensions 
} from "../api/activity";
import { stopThirtyDaysChallenge, getChallengeHistory } from "../api/thirty-day-challenge";
import Layout from '../components/common/Layout';

const StatCard = ({ icon: Icon, label, value }) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium">{label}</CardTitle>
      <Icon className="h-4 w-4 text-muted-foreground" />
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
    </CardContent>
  </Card>
);

const HistoryItem = ({ challenge }) => {
  const startDate = new Date(challenge.startDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const endDate = new Date(challenge.endDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'Completed';
      case 'ACTIVE':
        return 'In Progress';
      default:
        return 'Stopped';
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center justify-between p-4 rounded-lg border bg-card"
    >
      <div className="space-y-1">
        <div className="text-sm text-muted-foreground">
          {startDate} to {endDate}
        </div>
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">
            {getStatusDisplay(challenge.status)}
          </span>
        </div>
      </div>
      <div className={`flex items-center gap-2 ${
        challenge.status === 'COMPLETED' 
          ? 'text-primary'
          : challenge.status === 'ACTIVE'
          ? 'text-secondary'
          : 'text-destructive'
      }`}>
        {challenge.status === 'COMPLETED' ? (
          <CheckCircledIcon className="h-5 w-5" />
        ) : challenge.status === 'ACTIVE' ? (
          <TimerIcon className="h-5 w-5" />
        ) : (
          <CrossCircledIcon className="h-5 w-5" />
        )}
      </div>
    </motion.div>
  );
};

export default function ThirtyDayChallengePage() {
  const { activityId, challengeId } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [challengeData, setChallengeData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('current');
  const { toast } = useToast();

  useEffect(() => {
    document.title = "30 Days Challenge";
    fetchData();
  }, [activityId, challengeId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [activityData, extensionsData, historyData] = await Promise.all([
        getActivity(activityId),
        getActivityExtensions(activityId),
        getChallengeHistory(activityId)
      ]);
      
      setActivity(activityData);
      
      const challengeExtension = extensionsData.find(ext => ext.code === 'thirty_days_challenge');
      if (challengeExtension) {
        setChallengeData(challengeExtension.data);
      }
      
      setHistory(historyData);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to fetch challenge data',
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStopChallenge = async () => {
    if (!window.confirm('Are you sure you want to stop this challenge? Your progress will be saved in history.')) {
      return;
    }
    
    try {
      await stopThirtyDaysChallenge(activityId, challengeData.id);
      toast({
        title: "Success",
        description: "Challenge stopped",
        type: "success"
      });
      await fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to stop challenge',
        type: "error"
      });
    }
  };

  const renderCalendar = () => {
    if (!challengeData || !challengeData.days) return null;
    
    const days = challengeData.days;
    const today = new Date().toISOString().split('T')[0];

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Challenge Calendar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-1 mb-4">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="text-center text-sm font-medium text-muted-foreground">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            <AnimatePresence mode="popLayout">
              {generateCalendarGrid(days, today)}
            </AnimatePresence>
          </div>
        </CardContent>
      </Card>
    );
  };

  const generateCalendarGrid = (days, today) => {
    if (!days.length) return null;
    
    const startDate = new Date(days[0].date);
    const startDay = startDate.getDay();
    
    const emptyCells = Array(startDay).fill(null).map((_, index) => (
      <div key={`empty-start-${index}`} className="h-12" />
    ));
    
    const dayCells = days.map(day => {
      const dayDate = day.date;
      const isPast = dayDate < today;
      const isToday = dayDate === today;
      
      let statusClass = '';
      if (day.completed) {
        statusClass = 'bg-primary/10 text-primary dark:bg-primary/20';
      } else if (isPast) {
        statusClass = 'bg-destructive/10 text-destructive dark:bg-destructive/20';
      } else if (isToday) {
        statusClass = 'bg-secondary/10 text-secondary dark:bg-secondary/20 ring-2 ring-secondary';
      } else {
        statusClass = 'bg-muted/10 text-muted-foreground dark:bg-muted/20';
      }

      return (
        <motion.div
          key={day.dayNumber}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2 }}
          className={`flex flex-col items-center justify-center h-12 rounded-md ${statusClass}`}
          title={`Day ${day.dayNumber}: ${new Date(dayDate).toLocaleDateString()}`}
        >
          <span className="text-sm font-medium">
            {new Date(dayDate).getDate()}
          </span>
          {day.completed ? (
            <CheckIcon className="h-3 w-3" />
          ) : isPast ? (
            <Cross1Icon className="h-3 w-3" />
          ) : null}
        </motion.div>
      );
    });

    return [...emptyCells, ...dayCells];
  };

  const renderChallengeStats = () => {
    if (!challengeData || !challengeData.stats) return null;
    
    const { completedDays, totalDays, remainingDays, completionPercentage } = challengeData.stats;
    
    return (
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          icon={CheckCircledIcon}
          label="Days Completed"
          value={completedDays}
        />
        <StatCard
          icon={TimerIcon}
          label="Days Remaining"
          value={remainingDays}
        />
        <StatCard
          icon={ClockIcon}
          label="Completion"
          value={`${completionPercentage.toFixed(0)}%`}
        />
        <StatCard
          icon={CalendarIcon}
          label="Total Days"
          value={totalDays}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header Section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/activity/${activityId}/view`)}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                {activity ? `${activity.name} - 30 Days Challenge` : '30 Days Challenge'}
              </h1>
              <p className="text-muted-foreground">
                Track your 30-day challenge progress
              </p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList>
            <TabsTrigger value="current">Current Challenge</TabsTrigger>
            <TabsTrigger value="history">Challenge History</TabsTrigger>
          </TabsList>
          
          <TabsContent value="current">
            {challengeData && challengeData.status === 'ACTIVE' ? (
              <div className="space-y-8">
                {renderChallengeStats()}
                {renderCalendar()}
                <div className="flex justify-end">
                  <Button
                    variant="destructive"
                    onClick={handleStopChallenge}
                  >
                    <StopIcon className="mr-2 h-4 w-4" />
                    Stop Challenge
                  </Button>
                </div>
              </div>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <CalendarIcon className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-lg font-medium mb-6">
                    There is no active challenge for this activity.
                  </p>
                  <Button
                    variant="outline"
                    className="rounded-full min-w-[180px]"
                    onClick={() => navigate(`/activity/${activityId}/view`)}
                  >
                    <ArrowLeftIcon className="mr-2 h-4 w-4" />
                    Back to Activity
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          <TabsContent value="history">
            <div className="space-y-4">
              {history.length > 0 ? (
                history.map(challenge => (
                  <HistoryItem key={challenge.id} challenge={challenge} />
                ))
              ) : (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <ClockIcon className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      No previous challenges found.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
} 